import { useGetFile } from '@/hooks/useFiles';

interface UsePOEDataParams {
  riskKey: string | undefined;
  poeLocation: string;
  fallbackPOELocation: string | undefined;
  name: string;
}

interface UsePOEDataResult {
  file: unknown;
  fileStatus: 'pending' | 'success' | 'error';
  evidenceFileValue: string;
  /** True when the evidence/{riskKey} fetch genuinely FAILED (network/5xx —
   * not "no file yet", which resolves to ''). Lets the Verification pane
   * render an error state instead of the "No verification notes" empty
   * state. */
  evidenceLoadFailed: boolean;
}

export function usePOEData({
  riskKey,
  poeLocation,
  fallbackPOELocation,
}: UsePOEDataParams): UsePOEDataResult {
  // surfaceErrors: failures land in `isError` instead of coercing to '' so
  // they cannot masquerade as an empty evidence file (see useGetFile).
  const { data: evidenceFile, isError: evidenceLoadFailed } = useGetFile(
    { name: `evidence/${riskKey}` },
    { enabled: !!riskKey, surfaceErrors: true }
  );

  const evidenceFileValue =
    typeof evidenceFile === 'string'
      ? evidenceFile
      : evidenceFile
        ? JSON.stringify(evidenceFile)
        : '';

  const { data: poeFile, status: poeFileStatus } = useGetFile(
    { name: `proofs/${poeLocation}` },
    { enabled: !!poeLocation }
  );

  const enableFallback =
    !!fallbackPOELocation &&
    poeFileStatus !== 'pending' &&
    (poeFileStatus === 'error' || (poeFileStatus === 'success' && !poeFile));

  const { data: fallbackFile, status: fallbackStatus } = useGetFile(
    { name: `proofs/${fallbackPOELocation}` },
    { enabled: enableFallback }
  );

  const file = enableFallback ? fallbackFile : poeFile;
  const fileStatus = enableFallback ? fallbackStatus : poeFileStatus;

  return {
    file,
    fileStatus,
    evidenceFileValue,
    evidenceLoadFailed: Boolean(evidenceLoadFailed),
  };
}
